import React from 'react';
import i18next from 'i18next';
import {
    Card,
    CardActionArea,
    CardContent,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import { GlobalVariables } from '../../../../pdf/GlobalVariables';
import { dateTimeFormat, readableBytes } from '../../../../utils';
import { mdiDownload } from '@mdi/js';
import type { Core } from '@pdftron/webviewer';
import { ApryseSDK } from '../../../../pdf/ApryseSDK';
import Icon from '@mdi/react';

interface FileAttachmentMeta {
    attachment: Core.Annotations.FileAttachmentAnnotation;
    uniqueId: string;
    page: number;
    fileName: string;
    fileSize: number;
    created: string;
    offsetTop: number;
}

export interface EmbeddedFileAttachmentMeta {
    fileSpec: Core.PDFNet.FileSpec;
    fileName: string;
    fileSize: number;
}

export default () => {
    const [attachments, setAttachments] = React.useState<FileAttachmentMeta[]>([]);
    const [embeddedAttachments, setEmbeddedAttachments] = React.useState<EmbeddedFileAttachmentMeta[]>([]);

    React.useEffect(() => {
        const getAttachments = async () => {
            const allPagesAttachments: FileAttachmentMeta[] = [];
            ApryseSDK.attachments.forEach(async current => {
                const attachment = current as Core.Annotations.FileAttachmentAnnotation;
                const metas = attachment.getFileMetadata();
                const fileSize = (await attachment.getFileData()).size;
                allPagesAttachments.push({
                    attachment,
                    uniqueId: attachment.Id,
                    fileName: metas.filename,
                    fileSize,
                    page: attachment.PageNumber,
                    created: ApryseSDK.getCreationTime(current)?.toLocaleString(
                        'de-DE',
                        dateTimeFormat,
                    ),
                    offsetTop: current.getRect().getTop(),
                } as FileAttachmentMeta);
            });
            setAttachments(allPagesAttachments);
        };

        const getEmbeddedFiles = async () => {
            const embeddedFiles = await ApryseSDK.embeddedFiles();
            setEmbeddedAttachments(embeddedFiles);
        };

        getAttachments();
        getEmbeddedFiles();
    }, []);

    const onFileAttachmentClick = (attachmentMeta: FileAttachmentMeta) => {
        GlobalVariables.zoom(0, attachmentMeta.attachment);
        ApryseSDK.annotationManager.deselectAllAnnotations();
        ApryseSDK.annotationManager.selectAnnotation(attachmentMeta.attachment);
        // ApryseSDK.documentViewer.zoomTo(0, undefined, clickedAttachment.offsetTop);
    };

    const handleDownloadFile = async (attachmentMeta: FileAttachmentMeta) => {
        const downloadLink = document.createElement('a');
        downloadLink.download = attachmentMeta.fileName;
        const blob = await attachmentMeta.attachment.getFileData();

        const URL = window.URL || window.webkitURL;
        const downloadUrl = URL.createObjectURL(blob);

        downloadLink.href = downloadUrl;

        document.body.appendChild(downloadLink);

        downloadLink.click();

        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(downloadUrl);
    };

    const handleDownloadPdf = async (attachmentData: EmbeddedFileAttachmentMeta) => {
        const { PDFNet } = ApryseSDK.instance.Core;
        const downloadLink = document.createElement('a');
        downloadLink.download = attachmentData.fileName;
        const stm = await attachmentData.fileSpec.getFileData();

        const filterReader = await PDFNet.FilterReader.create(stm);
        const blobParts: BlobPart[] = [];
        const chunkLength = 1024;
        let retrievedLength = chunkLength;
        while (chunkLength === retrievedLength) {
            const bufferSubArray = await filterReader.read(chunkLength);
            retrievedLength = bufferSubArray.length;
            blobParts.push(bufferSubArray);
        }
        const blob = new Blob(blobParts);

        const URL = window.URL || window.webkitURL;
        const downloadUrl = URL.createObjectURL(blob);

        downloadLink.href = downloadUrl;

        document.body.appendChild(downloadLink);

        downloadLink.click();

        document.body.removeChild(downloadLink);

        URL.revokeObjectURL(downloadUrl);
    };

    const renderFileAttachment = (fileAttachment: FileAttachmentMeta) => (
        <Card sx={{ margin: '10px', padding: 0 }}>
            <CardActionArea onClick={() => onFileAttachmentClick(fileAttachment)} disableRipple>
                <CardContent sx={{ padding: 0, '&:last-child': { pb: 0 } }}>
                    <ListItem
                        secondaryAction={
                            <IconButton onClick={() => handleDownloadFile(fileAttachment)}>
                                <Icon path={mdiDownload} size={1.0} />
                            </IconButton>
                        }
                    >
                        <ListItemText
                            primary={fileAttachment.fileName}
                            secondary={
                                <Stack>
                                    <Typography variant="subtitle2">{`${i18next.t('attachmentsPanel.page')}: ${fileAttachment.page + 1}`}</Typography>
                                    <Typography variant="subtitle2">{`${i18next.t('attachmentsPanel.fileSize')}: ${readableBytes(fileAttachment.fileSize.toString())}`}</Typography>
                                    <Typography variant="subtitle2">{`${i18next.t('attachmentsPanel.created')}: ${fileAttachment.created}`}</Typography>
                                </Stack>
                            }
                        />
                    </ListItem>
                </CardContent>
            </CardActionArea>
        </Card>
    );

    const renderEmbeddedAttachment = (attachment: EmbeddedFileAttachmentMeta) => (
        <Card sx={{ margin: '10px', padding: 0 }}>
            <CardContent sx={{ padding: 0, '&:last-child': { pb: 0 } }}>
                <ListItem
                    secondaryAction={
                        <IconButton onClick={() => handleDownloadPdf(attachment)}>
                            <Icon path={mdiDownload} size={1.0} />
                        </IconButton>
                    }
                >
                    <ListItemText
                        primary={attachment.fileName}
                        secondary={`${i18next.t('attachmentsPanel.fileSize')}: ${readableBytes(attachment.fileSize.toString())}`}
                    />
                </ListItem>
            </CardContent>
        </Card>
    );

    return (
        <List
            sx={{ margin: 0, padding: 0, overflow: 'auto', position: 'relative', height: '100%' }}
        >
            {attachments.map(x => renderFileAttachment(x))}
            {embeddedAttachments.map(x => renderEmbeddedAttachment(x))}
        </List>
    );
};
